import Menu from "@mui/material/Menu";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { Box, Modal } from "@mui/material";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from '@mui/material/Tooltip';
import MenuItem from "@mui/material/MenuItem";
import ModalLayout from "../FormModel/ModalLayout";
import { configState } from "../../../../recoil/atoms";
import LinearProgress from "@mui/material/LinearProgress";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import InventoryTemplateModal from "../FormModel/InventoryTemplateModal";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { Button, Grid, Card, CardContent, Typography, Switch } from "@mui/material";
import { InventoryTemplateApi, Templates } from "../../../../api/medical-operations";
import { IconCirclePlus, IconDotsVertical, IconFile, IconPencil, IconTrash, IconNewSection } from "@tabler/icons";

const commonCardStyle = {
  border: '1px solid #ccc',
  borderRadius: 3,    
  mt: 2,
  width: "30%",

  '@media (max-width: 768px)': {
    width: "100%",
  },
};

export default function InventoryTemplates() {
  const navigate = useNavigate();  
  const config = useRecoilValue(configState);

  const [id, setId] = useState<string>("");  
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<Templates>();
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [templates, setTemplates] = useState<Templates[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [anchorElMap, setAnchorElMap] = useState<{ [key: string]: null | HTMLElement }>({});

  const { canEditTemplate, canCreateInventory, canCreateTemplate, canDeleteTemplate } = useAuthorization();
  
  const handleOpenMenu = (event: any, templateID: string) => {
    setAnchorElMap((prev) => ({ ...prev, [templateID]: event.currentTarget }));
  };

  const handleCloseMenu = (templateID: string) => {
    setAnchorElMap((prev) => ({ ...prev, [templateID]: null }));
  };

  const handleMenuItemClick = (action: any, template: any) => {
    const templateID = template.TemplateID;    
    handleCloseMenu(templateID);

    if (action === "edit") {
      setIsEdit(true);
      handleOpenModal();
      setEditModal(template);
    } else if (action === "createInventory") {
      setIsCreate(true);
      handleOpenModal();
      setEditModal(template);
    } else if (action === "deleteTemplate") {
      handleDelete(templateID);          
    }
  };

  const handleOpenModal = () => setOpenModal(true);

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setIsCreate(false);
    setEditModal(undefined);
    getTemplates();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setId(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deleteTemplate(id);
    handleCloseDialog();
  };

  const getTemplates = () => {
    const Templates = new InventoryTemplateApi(config);
    Templates.getAllTemplates()
      .then((res) => {
        setIsLoading(false);
        setTemplates(res.data);        
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const deleteTemplate = (templateID: string) => {
    const Templates = new InventoryTemplateApi(config);
    Templates.deleteTemplate(templateID)
      .then((res) => {
        getTemplates();
        toast.success(res.data.Message!);    
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getTemplates();    
  }, []);

  return (      
    <MainCard>
      <Grid>
        {!isLoading ? (
          <>            
            <Modal
              open={openModal}
              onClose={handleCloseModal}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Box>
                <ModalLayout>
                  <InventoryTemplateModal handleCloseModal={handleCloseModal} editTemplate={editModal} isEdit={isEdit} isCreate={isCreate} />
                </ModalLayout>
              </Box>
            </Modal>                
            <Box display="flex" justifyContent="flex-end">
              {canCreateTemplate && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<IconCirclePlus />}
                  onClick={()=>{
                    navigate("/create-template")
                  }}
                >
                  Create New Template                    
                </Button>
              )}
            </Box>
            {templates.length > 0 ? (
              <>
              <Box style={{ display: 'flex', flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
                {templates.map((template: any) => (
                  <Card key={template.TemplateID} sx={commonCardStyle}>
                    <CardContent>
                      <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>                        
                        <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography 
                                variant="h4" 
                                component="div" 
                                style={{ cursor: 'pointer' }} 
                                onClick={() => {
                                  navigate(`/collections/${template.TemplateID}`, {
                                    state: template.Description,
                                  });
                                }}
                              >
                                {template.TemplateName}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Tooltip title={template.Description} arrow>
                                <Typography 
                                  variant="body2" 
                                  component="div" 
                                  color="textSecondary"
                                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {template.Description}
                                </Typography>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Box>                        
                        <Box style={{ flex: '0 0 auto' }}>
                          <Button
                            startIcon={<IconDotsVertical />}
                            onClick={(event) => handleOpenMenu(event, template.TemplateID)}
                            color="secondary"
                          />
                          <Menu anchorEl={anchorElMap[template.TemplateID]} open={Boolean(anchorElMap[template.TemplateID])} onClose={() => handleCloseMenu(template.TemplateID)}>
                            {canEditTemplate && (
                              <MenuItem onClick={() => handleMenuItemClick("edit", template)}>
                                <IconPencil/> 
                                <Typography sx={{pl: 1}}>Edit</Typography>
                              </MenuItem>
                            )}
                            {canCreateInventory && (
                              <MenuItem onClick={() => handleMenuItemClick("createInventory", template)}>
                                <IconNewSection />
                                <Typography sx={{pl: 1}}>Create Inventory</Typography>                                
                              </MenuItem>
                            )}
                            {canDeleteTemplate && (
                              <MenuItem onClick={() => handleMenuItemClick("deleteTemplate", template)}>
                                <IconTrash color="red"/>
                                <Typography sx={{pl: 1, color: "red"}}>Delete Template</Typography>
                              </MenuItem>
                            )}
                          </Menu>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}                
              </Box>  
              <ConfirmDialog
                name={"Template"}
                handleCloseDialog={handleCloseDialog}
                handleConfirm={handleConfirm}
                openDialog={openDialog}
              />               
              </>
            ):(
              <Box textAlign="center" sx={{ mt: 10 }} >
                <IconFile size={100}/>
                <Typography variant="body2" color="textSecondary">
                  No Templates available yet.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Start by creating your own custom Inventory Template
                </Typography>
              </Box>
              )}              
          </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </Grid>
    </MainCard>    
  );
}
